/** Adresses d'expediteur du client pour « Prouver » (lot 3 ; decisions A et
 *  K de Franck du 06/10/2026) : ajout d'une adresse, notice DNS de son
 *  domaine, bouton « Vérifier ». Le client publie le DNS lui-meme. */
import { useCallback, useEffect, useState } from "react";

import { api, type Expediteurs } from "../api";
import { Erreur, date } from "../components/ui";

const ETATS: Record<string, { texte: string; classe: string }> = {
  checked: { texte: "Vérifié", classe: "pill--ok" },
  invalid: { texte: "DNS incorrect", classe: "pill--err" },
  revoked: { texte: "Révoqué", classe: "pill--err" },
  locked: { texte: "Bloqué", classe: "pill--err" },
};

export function ExpediteursProuver() {
  const [donnees, setDonnees] = useState<Expediteurs | null>(null);
  const [adresse, setAdresse] = useState("");
  const [enCours, setEnCours] = useState<string | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [copie, setCopie] = useState<string | null>(null);

  // Meme facon de copier que LiensATransmettre (B-07).
  const copier = async (texte: string) => {
    try {
      await navigator.clipboard.writeText(texte);
      setCopie(texte);
    } catch {
      setCopie(null);
    }
  };
  const BoutonCopier = ({ texte }: { texte: string }) => (
    <button type="button" className="btn btn--link" onClick={() => void copier(texte)}>
      {copie === texte ? "Copié" : "Copier"}
    </button>
  );

  const recharger = useCallback(async () => {
    setDonnees(await api.expediteurs());
  }, []);
  useEffect(() => {
    recharger().catch(setErreur);
  }, [recharger]);

  const agir = async (cle: string, action: () => Promise<unknown>) => {
    setEnCours(cle);
    setErreur(null);
    try {
      await action();
      await recharger();
    } catch (e) {
      setErreur(e);
    } finally {
      setEnCours(null);
    }
  };

  return (
    <div className="card stack" style={{ marginBottom: "1.5rem" }}>
      <h2 className="card__title" style={{ fontSize: "1.1rem" }}>Prouver · Adresses d'expéditeur</h2>
      <p className="muted" style={{ margin: 0 }}>
        Pour envoyer depuis votre propre adresse : ajoutez-la, publiez les enregistrements DNS indiqués chez
        l'hébergeur de votre domaine, puis cliquez sur « Vérifier » (la prise en compte du DNS peut demander
        quelques heures). Sans adresse vérifiée, l'e-mail part de l'adresse Sign365, à votre nom. Le choix de l'adresse se fait à chaque
        envoi, dans Prouver.
      </p>
      <form className="row" onSubmit={(e) => {
        e.preventDefault();
        if (adresse.trim()) void agir("ajout", () => api.ajouterExpediteur(adresse.trim()).then(() => setAdresse("")));
      }}>
        <div className="field">
          <label className="field-label" htmlFor="pe-exp-nouvelle">Nouvelle adresse</label>
          <input id="pe-exp-nouvelle" className="input" type="email" placeholder="contact@votre-domaine.fr"
                 value={adresse} onChange={(e) => setAdresse(e.target.value)} />
        </div>
        <div className="field" style={{ alignSelf: "end" }}>
          <button type="submit" className="btn btn--secondary" disabled={!adresse.trim() || enCours !== null}>
            {enCours === "ajout" ? "Ajout…" : "Ajouter"}
          </button>
        </div>
      </form>
      <Erreur erreur={erreur} />

      {donnees?.domaines.map((d) => {
        const etat = ETATS[d.statut] ?? { texte: "En attente du DNS", classe: "pill--pending" };
        return (
          <div key={d.id} className="stack">
            <div className="actions actions--split" style={{ margin: 0 }}>
              <strong>{d.nom} <span className={`pill ${etat.classe}`}>{etat.texte}</span></strong>
              <button type="button" className="btn btn--secondary" disabled={enCours !== null}
                      onClick={() => void agir(d.id, () => api.verifierDomaine(d.id))}>
                {enCours === d.id ? "Vérification…" : "Vérifier"}
              </button>
            </div>
            {d.verifie_le && <p className="muted" style={{ margin: 0 }}>Vérifié le {date(d.verifie_le, true)}.</p>}
            {d.derniere_erreur && <div className="alert alert--error">{d.derniere_erreur}</div>}
            {d.statut !== "checked" && (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Type</th><th>Nom</th><th>Valeur</th></tr></thead>
                  <tbody>
                    {d.dns.map((r) => (
                      <tr key={r.nom}>
                        <td>{r.type}</td>
                        <td><code>{r.nom}</code> <BoutonCopier texte={r.nom} /></td>
                        <td><code style={{ wordBreak: "break-all" }}>{r.valeur}</code> <BoutonCopier texte={r.valeur} />
                          <div className="muted">{r.note}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <ul style={{ margin: 0 }}>
              {donnees.adresses.filter((a) => a.domaine_id === d.id).map((a) => (
                <li key={a.id}>
                  {a.adresse}{" "}
                  <button type="button" className="btn btn--link" disabled={enCours !== null}
                          onClick={() => {
                            if (window.confirm(`Retirer l'adresse ${a.adresse} ?`)) {
                              void agir(a.id, () => api.supprimerExpediteur(a.id));
                            }
                          }}>
                    Retirer
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
